import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, download, qs } from '../lib/api'
import { count, dateOnly, duration, km, kph, minutes, pct } from '../lib/format'
import type { Fleet } from '../lib/types'
import { Page } from '../components/Shell'
import { Icon } from '../components/Icon'
import { DataTable, type Column } from '../components/DataTable'
import { Bar, Button, Chip, Metric, Panel, toast } from '../components/ui'

type ReportKey = 'trips' | 'idle' | 'scorecard' | 'geofence-attendance' | 'utilisation'

const REPORTS: { key: ReportKey; label: string; blurb: string }[] = [
  { key: 'trips', label: 'Trip summary', blurb: 'Distance, driving time and idle time per vehicle per day' },
  { key: 'idle', label: 'Idle analysis', blurb: 'Which vehicles burn fuel standing still' },
  { key: 'scorecard', label: 'Driver scorecard', blurb: 'Harsh events and speeding, normalised per 100 km' },
  { key: 'geofence-attendance', label: 'Geofence attendance', blurb: 'Arrivals, departures and dwell time' },
  { key: 'utilisation', label: 'Utilisation', blurb: 'Which vehicles are earning their keep' },
]

const RANGES = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: '365d', label: '12 months' },
]

export function Reports() {
  const [report, setReport] = useState<ReportKey>('trips')
  const [range, setRange] = useState('30d')
  const [fleet, setFleet] = useState('')

  const fleets = useQuery({ queryKey: ['fleets'], queryFn: () => api<{ items: Fleet[] }>('/fleets') })

  const query = useQuery({
    queryKey: ['report', report, range, fleet],
    queryFn: () =>
      api<{ from: string; to: string; rows: unknown[]; totals?: Record<string, number>; note?: string }>(
        `/reports/${report}${qs({ range, fleetId: fleet })}`,
      ),
  })

  const meta = REPORTS.find((r) => r.key === report)!

  return (
    <Page
      title="Reports"
      subtitle={meta.blurb}
      actions={
        <Button
          onClick={() =>
            download(`/reports/${report}${qs({ range, fleetId: fleet, format: 'csv' })}`, `${report}.csv`)
              .then(() => toast('Export downloaded', 'ok'))
              .catch((e: Error) => toast(e.message, 'error'))
          }
        >
          <Icon name="download" size={13} />
          Export CSV
        </Button>
      }
    >
      <div className="grid lg:grid-cols-[220px_1fr] gap-4 items-start">
        {/* ---- report picker ---- */}
        <div className="space-y-3">
          <Panel padded={false}>
            <ul>
              {REPORTS.map((r) => (
                <li key={r.key}>
                  <button
                    onClick={() => setReport(r.key)}
                    className="w-full text-left px-3 py-2.5 rule-b last:border-b-0 transition-colors"
                    style={{
                      background: report === r.key ? 'var(--color-panel-2)' : 'transparent',
                      borderLeft: report === r.key ? '2px solid var(--accent)' : '2px solid transparent',
                    }}
                  >
                    <div
                      className="text-[13px]"
                      style={{ color: report === r.key ? 'var(--accent)' : 'var(--color-ink-2)' }}
                    >
                      {r.label}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </Panel>

          <Panel title="Filters">
            <div className="space-y-3">
              <div>
                <span className="label block mb-1.5">Period</span>
                <div className="grid grid-cols-2 gap-1.5">
                  {RANGES.map((r) => (
                    <button
                      key={r.key}
                      onClick={() => setRange(r.key)}
                      className="h-7 rounded-[3px] border text-[11px] transition-colors"
                      style={{
                        borderColor: range === r.key ? 'var(--accent)' : 'var(--color-rule)',
                        background: range === r.key ? 'var(--accent-dim)' : 'transparent',
                        color: range === r.key ? 'var(--accent-text)' : 'var(--color-ink-3)',
                      }}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className="label block mb-1.5">Fleet</span>
                <select className="field" value={fleet} onChange={(e) => setFleet(e.target.value)}>
                  <option value="">All fleets</option>
                  {fleets.data?.items.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </Panel>

          <p className="text-[11px] text-[var(--color-ink-4)] leading-relaxed px-1">
            Reports read pre-aggregated daily rollups, so a twelve-month window costs about the same
            as a week.
          </p>
        </div>

        {/* ---- results ---- */}
        <div className="space-y-3 min-w-0">
          {query.data?.from && (
            <div className="flex items-center gap-2 text-[12px] text-[var(--color-ink-3)]">
              <Icon name="clock" size={13} />
              {dateOnly(query.data.from)} — {dateOnly(query.data.to)}
              <span className="grow" />
              {query.data.rows && <span className="tnum">{count(query.data.rows.length)} rows</span>}
            </div>
          )}

          {report === 'idle' && query.data?.totals && (
            <div className="grid grid-cols-2 gap-3">
              <Metric
                label="Total idle time"
                value={minutes(query.data.totals.idleMinutes)}
                tone="idle"
              />
              <Metric
                label="Estimated wasted fuel"
                value={query.data.totals.estimatedWastedLitres?.toFixed(0) ?? '—'}
                unit="litres"
                tone="critical"
                hint={query.data.note}
              />
            </div>
          )}

          <Panel padded={false}>
            <ReportTable report={report} query={query} />
          </Panel>
        </div>
      </div>
    </Page>
  )
}

/* ===========================================================================
   Per-report columns
   =========================================================================== */

interface TripRow {
  day: string
  vehicleName: string
  fleetName?: string
  trips: number
  distanceKm: number
  drivingMinutes: number
  idleMinutes: number
  maxSpeedKph?: number
  avgScore?: number
}

interface IdleRow {
  vehicleId: string
  vehicleName: string
  fleetName?: string
  idleMinutes: number
  drivingMinutes: number
  idleSharePct: number
  trips: number
  estimatedWastedLitres: number
}

interface ScoreRow {
  driverId: string
  driverName: string
  rank: number
  trips: number
  distanceKm: number
  harshAccel: number
  harshBrake: number
  harshCorner: number
  overspeedMinutes: number
  eventsPer100Km: number
  score?: number
}

interface AttendanceRow {
  geofenceName: string
  vehicleName: string
  driverName?: string
  enteredAt: string
  exitedAt?: string
  dwellMinutes?: number
}

interface UtilisationRow {
  vehicleId: string
  vehicleName: string
  registration?: string
  fleetName?: string
  activeDays: number
  totalDays: number
  utilisationPct: number
  distanceKm: number
  trips: number
}

function ReportTable({
  report,
  query,
}: {
  report: ReportKey
  query: ReturnType<typeof useQuery<{ rows: unknown[] }>>
}) {
  const rows = query.data?.rows ?? []

  const shared = {
    loading: query.isLoading,
    error: query.error,
    onRetry: () => void query.refetch(),
    empty: {
      title: 'No data for this period',
      body: 'Either nothing was recorded, or the daily rollups have not caught up yet — they refresh hourly.',
    },
  }

  switch (report) {
    case 'trips': {
      const columns: Column<TripRow>[] = [
        { key: 'day', header: 'Day', width: '110px', render: (r) => dateOnly(r.day) },
        { key: 'vehicle', header: 'Vehicle', render: (r) => r.vehicleName },
        { key: 'fleet', header: 'Fleet', render: (r) => r.fleetName ?? '—' },
        { key: 'trips', header: 'Trips', numeric: true, render: (r) => r.trips },
        { key: 'distance', header: 'Distance', numeric: true, render: (r) => km(r.distanceKm * 1000) },
        { key: 'driving', header: 'Driving', numeric: true, render: (r) => minutes(r.drivingMinutes) },
        {
          key: 'idle',
          header: 'Idle',
          numeric: true,
          render: (r) => (
            <span style={{ color: r.idleMinutes > 60 ? 'var(--text-idle)' : undefined }}>
              {minutes(r.idleMinutes)}
            </span>
          ),
        },
        { key: 'max', header: 'Max', numeric: true, render: (r) => kph(r.maxSpeedKph) },
      ]
      return <DataTable {...shared} columns={columns} rows={rows as TripRow[]} rowKey={(r) => `${r.day}-${r.vehicleName}`} />
    }

    case 'idle': {
      const worst = Math.max(...(rows as IdleRow[]).map((r) => r.idleMinutes), 1)
      const columns: Column<IdleRow>[] = [
        { key: 'vehicle', header: 'Vehicle', render: (r) => r.vehicleName },
        { key: 'fleet', header: 'Fleet', render: (r) => r.fleetName ?? '—' },
        {
          key: 'idle',
          header: 'Idle time',
          width: '180px',
          render: (r) => (
            <div className="space-y-1">
              <div className="flex justify-between tnum text-[12px]">
                <span>{minutes(r.idleMinutes)}</span>
                <span className="text-[var(--color-ink-4)]">{pct(r.idleSharePct)}</span>
              </div>
              <Bar value={r.idleMinutes} max={worst} tone={r.idleSharePct > 30 ? 'critical' : 'warn'} />
            </div>
          ),
        },
        { key: 'driving', header: 'Driving', numeric: true, render: (r) => minutes(r.drivingMinutes) },
        { key: 'trips', header: 'Trips', numeric: true, render: (r) => r.trips },
        {
          key: 'fuel',
          header: 'Est. wasted',
          numeric: true,
          render: (r) => `${r.estimatedWastedLitres.toFixed(1)} L`,
        },
      ]
      return <DataTable {...shared} columns={columns} rows={rows as IdleRow[]} rowKey={(r) => r.vehicleId} />
    }

    case 'scorecard': {
      const columns: Column<ScoreRow>[] = [
        {
          key: 'rank',
          header: '#',
          width: '44px',
          numeric: true,
          render: (r) => <span className="text-[var(--color-ink-4)]">{r.rank}</span>,
        },
        { key: 'driver', header: 'Driver', render: (r) => r.driverName },
        {
          key: 'score',
          header: 'Score',
          width: '110px',
          render: (r) =>
            r.score == null ? (
              '—'
            ) : (
              <div className="flex items-center gap-2">
                <span className="tnum font-semibold" style={{ color: scoreColor(r.score) }}>
                  {Math.round(r.score)}
                </span>
                <div className="grow">
                  <Bar value={r.score} tone={r.score >= 85 ? 'ok' : r.score >= 65 ? 'warn' : 'critical'} />
                </div>
              </div>
            ),
        },
        { key: 'distance', header: 'Distance', numeric: true, render: (r) => km(r.distanceKm * 1000, 0) },
        { key: 'trips', header: 'Trips', numeric: true, render: (r) => r.trips },
        {
          key: 'harsh',
          header: 'Harsh (A/B/C)',
          numeric: true,
          render: (r) => (
            <span className="tnum">
              {r.harshAccel}/{r.harshBrake}/{r.harshCorner}
            </span>
          ),
        },
        {
          key: 'per100',
          header: 'per 100 km',
          numeric: true,
          render: (r) => (
            <span style={{ color: r.eventsPer100Km > 5 ? 'var(--text-warn)' : undefined }}>
              {r.eventsPer100Km.toFixed(1)}
            </span>
          ),
        },
        { key: 'over', header: 'Speeding', numeric: true, render: (r) => minutes(r.overspeedMinutes) },
      ]
      return <DataTable {...shared} columns={columns} rows={rows as ScoreRow[]} rowKey={(r) => r.driverId} />
    }

    case 'geofence-attendance': {
      const columns: Column<AttendanceRow>[] = [
        { key: 'fence', header: 'Geofence', render: (r) => r.geofenceName },
        { key: 'vehicle', header: 'Vehicle', render: (r) => r.vehicleName },
        { key: 'driver', header: 'Driver', render: (r) => r.driverName ?? '—' },
        {
          key: 'in',
          header: 'Entered',
          numeric: true,
          render: (r) => new Date(r.enteredAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }),
        },
        {
          key: 'out',
          header: 'Left',
          numeric: true,
          render: (r) =>
            r.exitedAt ? (
              new Date(r.exitedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
            ) : (
              <Chip tone="accent">Still inside</Chip>
            ),
        },
        {
          key: 'dwell',
          header: 'Dwell',
          numeric: true,
          render: (r) => (r.dwellMinutes != null ? duration(r.dwellMinutes * 60) : '—'),
        },
      ]
      return (
        <DataTable
          {...shared}
          columns={columns}
          rows={rows as AttendanceRow[]}
          rowKey={(r) => `${r.geofenceName}-${r.vehicleName}-${r.enteredAt}`}
        />
      )
    }

    case 'utilisation': {
      const columns: Column<UtilisationRow>[] = [
        {
          key: 'vehicle',
          header: 'Vehicle',
          render: (r) => (
            <div>
              <div className="text-[var(--color-ink)]">{r.vehicleName}</div>
              {r.registration && (
                <div className="text-[11px] text-[var(--color-ink-4)]">{r.registration}</div>
              )}
            </div>
          ),
        },
        { key: 'fleet', header: 'Fleet', render: (r) => r.fleetName ?? '—' },
        {
          key: 'util',
          header: 'Utilisation',
          width: '190px',
          render: (r) => (
            <div className="space-y-1">
              <div className="flex justify-between tnum text-[12px]">
                <span>{pct(r.utilisationPct)}</span>
                <span className="text-[var(--color-ink-4)]">
                  {r.activeDays}/{r.totalDays} days
                </span>
              </div>
              <Bar
                value={r.utilisationPct}
                tone={r.utilisationPct < 25 ? 'critical' : r.utilisationPct < 55 ? 'warn' : 'ok'}
              />
            </div>
          ),
        },
        { key: 'distance', header: 'Distance', numeric: true, render: (r) => km(r.distanceKm * 1000, 0) },
        { key: 'trips', header: 'Trips', numeric: true, render: (r) => r.trips },
      ]
      return <DataTable {...shared} columns={columns} rows={rows as UtilisationRow[]} rowKey={(r) => r.vehicleId} />
    }
  }
}

function scoreColor(score: number) {
  if (score >= 85) return 'var(--text-moving)'
  if (score >= 65) return 'var(--text-idle)'
  return 'var(--text-stopped)'
}
